import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { Check, Sparkles, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { endpoints } from "../lib/api";

const TIERS = [
  {
    name: "Free",
    priceUsd: 0,
    period: "forever",
    description: "Perfect for casually exploring names and prices.",
    cta: "Get started",
    features: [
      "Unlimited price comparisons",
      "5 AI Name Finder searches / month",
      "Browse premium domains",
      "Basic insights dashboard",
      "Save up to 10 domains",
    ],
    highlight: false,
  },
  {
    name: "Pro",
    priceUsd: 12,
    period: "/ month",
    description: "For founders shipping their next brand.",
    cta: "Upgrade to Pro",
    features: [
      "Unlimited AI Name Finder searches",
      "Premium domain inside-track",
      "Price drop alerts",
      "CSV export of results",
      "Unlimited saves & watchlists",
      "Priority email support",
    ],
    highlight: true,
  },
];

export default function Pricing() {
  const { requestLogin, user } = useAuth();
  const { formatPrice, currency } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!user) { setPlan(null); return; }
    endpoints.billingMe()
      .then((r) => setPlan(r.data.plan))
      .catch(() => setPlan(null));
  }, [user]);

  const startCheckout = async (tier) => {
    if (tier.name === "Free") {
      if (!user) requestLogin("/dashboard");
      else navigate("/dashboard");
      return;
    }
    // Pro tier
    if (!user) {
      toast("Sign in first", { description: "Free with Google — takes 2 seconds." });
      requestLogin("/pricing");
      return;
    }
    if (plan === "pro") {
      toast.success("You're already a Pro member");
      navigate("/dashboard");
      return;
    }
    if (!window.Razorpay) {
      toast.error("Payment library failed to load. Please reload the page.");
      return;
    }
    setLoading(true);
    try {
      // Indian users default to INR pricing, rest of the world USD.
      const isINR = currency === "INR";
      const orderCurrency = isINR ? "INR" : "USD";
      const { data: order } = await endpoints.createOrder(orderCurrency);
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Dompryce",
        description: "Pro plan — unlimited AI Name Finder, price-drop alerts & more",
        image: "/logo.png",
        prefill: { email: order.user.email, name: order.user.name },
        theme: { color: "#4f46e5" },
        handler: async (resp) => {
          try {
            await endpoints.verifyPayment({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setPlan("pro");
            toast.success("Welcome to Dompryce Pro 🎉", {
              description: "Unlimited AI Name Finder searches unlocked.",
            });
            navigate("/dashboard");
          } catch (e) {
            toast.error("Payment verification failed", { description: e.response?.data?.detail || "Please contact support@dompryce.com." });
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed", { description: resp.error?.description || "Try a different method." });
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start checkout", { description: e.response?.data?.detail || "Please try again." });
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="Pricing — Free & Pro plans"
        description="Dompryce is free for unlimited domain price comparisons. Upgrade to Pro for unlimited AI Name Finder searches, price-drop alerts and watchlists."
        path="/pricing"
      />
      <section data-testid="pricing-page">
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Pricing</div>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">
            Simple, transparent pricing.
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-zinc-600">
            Start free. Upgrade when you're ready to ship the brand.
          </p>
          {plan === "pro" && (
            <div className="mx-auto mt-5 inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700" data-testid="pro-badge">
              <Crown className="h-3.5 w-3.5" /> You're on Pro
            </div>
          )}
        </div>

        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
          {TIERS.map((t) => {
            const isCurrentPro = plan === "pro" && t.name === "Pro";
            const isCurrentFree = (!plan || plan === "free") && t.name === "Free";
            return (
              <div
                key={t.name}
                data-testid={`pricing-${t.name}`}
                className={`relative rounded-3xl border bg-white p-8 transition-all ${
                  t.highlight
                    ? "border-indigo-300 ring-2 ring-indigo-100 shadow-lg"
                    : "border-zinc-200 soft-shadow"
                }`}
              >
                {t.highlight && (
                  <>
                    <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent beam" />
                    <div className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-indigo-600 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                      <Sparkles className="h-3 w-3" /> Most popular
                    </div>
                  </>
                )}
                <h3 className="font-display text-xl font-semibold text-zinc-900">{t.name}</h3>
                <p className="mt-1 text-sm text-zinc-500">{t.description}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold tracking-tight text-zinc-900 tabular-nums whitespace-nowrap">{formatPrice(t.priceUsd)}</span>
                  <span className="text-sm text-zinc-500 whitespace-nowrap">{t.period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${t.highlight ? "text-indigo-600" : "text-emerald-600"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startCheckout(t)}
                  disabled={loading || isCurrentPro || isCurrentFree}
                  data-testid={`pricing-cta-${t.name}`}
                  className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                    t.highlight
                      ? "bg-indigo-600 text-white hover:bg-indigo-500"
                      : "bg-zinc-900 text-white hover:bg-zinc-800"
                  }`}
                >
                  {loading && t.name === "Pro" && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isCurrentPro ? "Current plan" : isCurrentFree ? "Current plan" : t.cta}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-zinc-100 bg-zinc-50/60 p-6 text-center">
          <p className="text-sm text-zinc-600">
            Pro includes everything in Free. Cancel anytime. No hidden fees.
          </p>
          <p className="mt-2 text-xs text-zinc-400">
            Billed in <span className="font-mono">{currency === "INR" ? "INR (₹999)" : "USD ($12)"}</span> · Secure payments powered by Razorpay · Cards, UPI, Netbanking, Wallets supported.
          </p>
        </div>
      </section>
    </Layout>
  );
}
